// Angular
import { Component, OnInit } from '@angular/core';
import { HelperService } from 'src/app/_services/helper.service';
import { ProductsService } from 'src/app/_services/products.service';
import { LowStockEmailAlert, LowStockEmailAlertType, OverviewService } from 'src/app/_services/overview.service';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserActions } from 'src/app/states/user/user.actions';
import { StoresManager } from 'src/app/_global/stores-manager';

@UntilDestroy()
@Component({
	selector: 'kt-topbar',
	templateUrl: './topbar.component.html',
	styleUrls: ['./topbar.component.scss'],
})
export class TopbarComponent implements OnInit {
	userFullName: string;
	storesNotSynching: string;
	walmartSellerStoresMissingRefreshToken: string;
	walmartSellerStoresRefreshTokenExpiring: string;
	readonly lowStockEmailAlertType = LowStockEmailAlertType;
	fixedStockLowStockAlerts: LowStockEmailAlert[] = [];
	constructor(
		private stateStore: Store,
		private helperService: HelperService,
		private productsService: ProductsService,
		private overviewService: OverviewService,
		private router: Router,
		private stores: StoresManager) {	}

	get hasSyncAlerts() {
		return !!(this.storesNotSynching
			|| this.walmartSellerStoresMissingRefreshToken
			|| this.walmartSellerStoresRefreshTokenExpiring
			|| this.fixedStockLowStockAlerts.length);
	}

	ngOnInit() {
		let user = this.helperService.currentUser;
		if (user)
			this.userFullName = `${user.firstName} ${user.lastName}`;
		this.stores.getAll(storeEntities => {
			this.storesNotSynching = storeEntities
				.filter(p => p.syncOrNot == false)
				.map(({ name, type }) => `${name}(${type})`)
				.join(' - ');
		});
		// Only request the low-stock alerts when the user can access them. The endpoint is guarded by
		// the InventoryOrOverviewLowStockItems policy (Inventory OR OverviewLowStockItems), so calling it
		// unconditionally 403s users without either permission and shows an error toast on every app load.
		// hasPermission is AND (every), so check each permission separately to mirror the policy's OR.
		if (this.helperService.hasPermission(['Inventory']) || this.helperService.hasPermission(['OverviewLowStockItems'])) {
			this.overviewService
				.getFixedStockLowStockAlerts()
				.pipe(untilDestroyed(this))
				.subscribe(alerts => this.fixedStockLowStockAlerts = alerts || []);
		}
		this.overviewService
			.getWalmartSellerStoresMissingRefreshToken()
			.pipe(untilDestroyed(this))
			.subscribe(storeNames => {
				this.walmartSellerStoresMissingRefreshToken = (storeNames || []).join(' - ');
			});
		this.overviewService
			.getWalmartSellerStoresRefreshTokenExpiring()
			.pipe(untilDestroyed(this))
			.subscribe(storeNames => {
				this.walmartSellerStoresRefreshTokenExpiring = (storeNames || []).join(' - ');
			});
	}

	signOut() {
		this.stateStore.dispatch(new UserActions.Logout).subscribe(_ => window.location.href = '');
	}

	toggle(panelId: string) {
		let panel = document.getElementById(panelId);
		let panelClass = panelId.replace(/_/g, '-');
		if (panel.className == panelClass)
			panel.className += ` ${panelClass}--on`;
		else
			panel.className = panelClass;
	}

	dismissFixedStockLowStockAlert(alert: LowStockEmailAlert, event: Event) {
		event.stopPropagation();
		this.overviewService.dismissFixedStockLowStockAlert(alert)
			.pipe(untilDestroyed(this))
			.subscribe(() => this.fixedStockLowStockAlerts = this.fixedStockLowStockAlerts.filter(item => !this.isSameFixedStockLowStockAlert(item, alert)));
	}

	private isSameFixedStockLowStockAlert(left: LowStockEmailAlert, right: LowStockEmailAlert) {
		return left.productId === right.productId
			&& left.alertType === right.alertType
			&& left.favoriteStoreId === right.favoriteStoreId;
	}

	searchChanged(e: any, search: any) {
		this.productsService.getIdBySku(e.target.value).subscribe(id => {
			e.target.value = '';
			this.router.navigate(['/'], { skipLocationChange: true }).then(() =>
				this.router.navigate([`/products/${id}`]));
			search.classList.remove('show');
		});
	}
}
